import { ChangeDetectorRef } from '@angular/core';
import { merge as observableMerge, Observable, Observer, Subscription } from 'rxjs';
import { share } from 'rxjs/operators';
import { subscribeFilterFieldToTranslator, subscribeFilterSectionToTranslator } from './filter.translation';

export type FilterFieldType =
  | 'checkbox'
  | 'search-select'
  | 'date-picker'
  | 'items-and-status'
  | 'multi-items-and-status'
  | 'multi-select'
  | 'tabbed'
  | 'range'
  | 'radio'
  | 'text';

export type FilterSectionType = 'or' | 'and';

export class FilterSection {
  title?: string | null;
  type: FilterSectionType;
  fields: FilterField<any>[];
  collapsed: boolean;
  hideSection?: boolean;
  hideSelectors?: boolean;
  fieldValuesObservable: Observable<FilterField<any>>;
  fieldOptionsObservable: Observable<FilterField<any>>;

  constructor(options: { title?: string; type: FilterSectionType; fields: FilterField<any>[]; collapsed?: boolean }) {
    this.title = options.title || null;
    this.type = options.type;
    this.fields = options.fields || [];
    this.collapsed = options.collapsed || false;
    this.fieldValuesObservable = this.fields
      .map((field) => field.valueObservable)
      .reduce((previous, next) => observableMerge(previous, next));
    this.fieldOptionsObservable = this.fields
      .map((field) => field.optionsObservable)
      .reduce((previous, next) => observableMerge(previous, next));
  }

  toggleCollapsed(): void {
    this.collapsed = !this.collapsed;
  }

  subscribeToTranslator(
    translator: {
      stream: (key: string | Array<string>) => Observable<string | any>;
      instant: (key: string | Array<string>) => string | any;
    },
    cd: ChangeDetectorRef,
    attachToFields: boolean,
  ): Subscription[] {
    return subscribeFilterSectionToTranslator(this, translator, cd, attachToFields);
  }
}

// An icon associated with a filter field
export interface FilterFieldIcon {
  url: string;
  width?: number;
  height?: number;
}

export interface FilterFieldConfig {
  name: string;
  value: any;
  options?: any[];
  condition?: (item: any, value: any) => boolean;
  id?: string;
  appliedTextPrefix?: string;
  suffix?: string;
  icon?: FilterFieldIcon; // Currently only supported by checkbox fields
  disableTranslations?: boolean;
}

export abstract class FilterField<T> {
  name: string;
  type: FilterFieldType;
  condition?: (item: any, value: T) => boolean;
  id: string;
  appliedTextPrefix?: string | null;
  suffix: string;
  value: T | null;
  valueObservable: Observable<FilterField<T>>;
  valueChanged: (value: FilterField<T>) => void;
  options: any[];
  optionsObservable: Observable<FilterField<T>>;
  optionsChanged: (value: FilterField<T>) => void;
  icon?: FilterFieldIcon; // Currently only supported by checkbox fields
  disableTranslation?: boolean;

  constructor(config: FilterFieldConfig) {
    this.name = config.name;
    this.value = config.value;
    this.options = config.options || [];
    this.condition = config.condition;
    this.id = config.id || config.name.toLowerCase().split(' ').join('_');
    this.appliedTextPrefix = config.appliedTextPrefix || null;
    this.valueObservable = Observable.create((observer: Observer<FilterField<T>>) => {
      this.valueChanged = (value: FilterField<T>) => {
        observer.next(value);
      };
    }).pipe(share());
    this.optionsObservable = Observable.create((observer: Observer<FilterField<T>>) => {
      this.optionsChanged = (value: FilterField<T>) => {
        observer.next(value);
      };
    }).pipe(share());
    this.suffix = config.suffix || '';
    this.icon = config.icon;
    this.disableTranslation = config.disableTranslations || false;
  }

  public changeValue(value?: any): void {
    this.value = value;
    if (this.valueChanged) {
      this.valueChanged(this);
    }
  }

  public clearValue(): void {
    this.value = null;
    if (this.valueChanged) {
      this.valueChanged(this);
    }
  }

  public setOptions(options: any[]): void {
    this.options = options;
    if (this.optionsChanged) {
      this.optionsChanged(this);
    }
  }

  // This function is used to specify how to stringify the field value
  serializeValue(): string {
    return JSON.stringify(this.value);
  }

  /*
  Note: If overriding on a child class, special checking might need to be done to ensure the value from the url is valid.
  If not, this function should return null */
  deserializeValue(urlParam: string): T | null {
    return JSON.parse(urlParam);
  }

  appliedFieldDisplayText(value?: any): string {
    if (this.appliedTextPrefix) {
      return `${this.appliedTextPrefix}: ${this.appliedFieldText(value)}`;
    } else {
      return this.appliedFieldText(value);
    }
  }

  protected abstract appliedFieldText(value?: any): string;

  /**
   * @see subscribeFilterFieldToTranslator
   */
  subscribeToTranslator(
    translator: {
      stream: (key: string | Array<string>) => Observable<string | any>;
      instant: (key: string | Array<string>) => string | any;
    },
    cd: ChangeDetectorRef,
  ): Subscription[] {
    return subscribeFilterFieldToTranslator(this, translator, cd);
  }
}

export class Filters {
  id: string;
  fieldValuesObservable: Observable<FilterField<any>>;
  fieldOptionsObservable: Observable<FilterField<any>>;

  constructor(public title: string, public innerSections: FilterSection[]) {
    this.id = title.toLowerCase().replace(' ', '_');
    this.fieldValuesObservable = this.innerSections
      .map((section) => section.fieldValuesObservable)
      .reduce((previous, next) => observableMerge(previous, next));
    this.fieldOptionsObservable = this.innerSections
      .map((section) => section.fieldOptionsObservable)
      .reduce((previous, next) => observableMerge(previous, next));
  }

  public applyFilters(items: any[]): any[] {
    return items.filter((item) => this.applyFiltersToItem(item));
  }

  get sections(): FilterSection[] {
    return this.innerSections.filter((section) => !section.hideSection);
  }

  get fields(): FilterField<any>[] {
    return this.sections.map((section) => section.fields).reduce((allFields, fields) => allFields.concat(fields));
  }

  get activeFields(): FilterField<any>[] {
    return this.fields.filter((field) => {
      // If its a list make sure its empty
      if (Array.isArray(field.value) && field.value.length === 0) {
        return false;
      }
      // If its an object check that it's not just an empty object ({})
      if (!!field.value && typeof field.value === 'object' && Object.getOwnPropertyNames(field.value).length === 0) {
        // A Date object is also considered 'Empty', check to make sure its not just a date object
        return field.value instanceof Date;
      }
      // Otherwise truthyness should suffice
      return !!field.value;
    });
  }

  public clearAll(): void {
    this.fields.forEach((field) => field.clearValue());
  }

  public setValueForField(value: any, field: FilterField<any>): void {
    this.fields.find((f) => f === field)!.changeValue(value);
  }

  public setValueForFieldname(value: any, fieldname: string): void {
    this.fields.find((field) => field.name === fieldname)!.value = value;
  }

  private applyFiltersToItem(item: any): boolean {
    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i];
      if (!this.filterBySection(item, section)) {
        return false;
      }
    }
    return true;
  }

  private filterBySection(item: any, section: FilterSection): boolean {
    let doesPass = true;
    for (let i = 0; i < section.fields.length; i++) {
      const field = section.fields[i];
      if (!!field.value && !!field.condition) {
        doesPass = field.condition(item, field.value);
        if (!doesPass && section.type === 'and') {
          break;
        } else if (doesPass && section.type === 'or') {
          break;
        }
      }
    }
    return doesPass;
  }
}
